// Change #100101 - what a kiosk tablet can report about itself, for the Debug
// tab in Settings > Clock-In Kiosks and the (i) panel on the tablet.
//
// A web page only sees what the browser chooses to expose. Deliberately NOT
// available to any web page, so never shown as blank-but-coming:
//   - local / LAN IP (browsers hide it, WebRTC gives an mDNS placeholder)
//   - serial number, IMEI, MAC address
// The public IP is added by the server from the request, not collected here.

import version from "../../../version.json";

// Brand from the model code Android reports (e.g. "SM-X200" -> Samsung).
const MAKE_BY_MODEL_PREFIX = [
  [/^SM-|^GT-|^Galaxy/i, "Samsung"],
  [/^Pixel/i, "Google"],
  [/^Lenovo|^TB-/i, "Lenovo"],
  [/^Redmi|^Mi |^M2\d{3}|^2\d{3}[A-Z]/i, "Xiaomi"],
  [/^moto/i, "Motorola"],
  [/^KF|^Kindle/i, "Amazon"],
  [/^Nokia/i, "Nokia"],
  [/^HUAWEI|^MatePad/i, "Huawei"],
  [/^OPPO|^CPH/i, "Oppo"],
];

const guessMake = (model, userAgent) => {
  if (/iPad|iPhone|Macintosh/.test(userAgent)) return "Apple";
  for (const [pattern, make] of MAKE_BY_MODEL_PREFIX) {
    if (model && pattern.test(model)) return make;
  }
  return null;
};

const browserFromUa = (ua) => {
  const m =
    ua.match(/(Edg|SamsungBrowser|OPR|Firefox|Chrome|CriOS|Version)\/([\d.]+)/) || [];
  const names = { Edg: "Edge", OPR: "Opera", CriOS: "Chrome", Version: "Safari" };
  return m[1] ? `${names[m[1]] || m[1]} ${m[2]}` : null;
};

// Each probe is optional: one browser lacking an API must not lose the rest.
const safe = async (fn) => {
  try {
    return await fn();
  } catch {
    return null;
  }
};

export const collectDeviceInfo = async ({ deviceIdentifier } = {}) => {
  const nav = window.navigator || {};
  const ua = nav.userAgent || "";

  // User-Agent Client Hints (Chrome / Edge / Samsung Internet): the only way a
  // page learns the real model code on Android.
  const hints = await safe(() =>
    nav.userAgentData?.getHighEntropyValues?.([
      "model",
      "platform",
      "platformVersion",
      "architecture",
      "bitness",
      "fullVersionList",
    ])
  );

  const model = hints?.model || null;
  const battery = await safe(async () => {
    const b = await nav.getBattery?.();
    return b ? { levelPercent: Math.round(b.level * 100), charging: !!b.charging } : null;
  });
  const storage = await safe(async () => {
    const e = await nav.storage?.estimate?.();
    return e ? { usedMb: Math.round((e.usage || 0) / 1048576), quotaMb: Math.round((e.quota || 0) / 1048576) } : null;
  });
  // Count only: labels need camera permission and are not needed here.
  const cameras = await safe(async () => {
    const devices = await nav.mediaDevices?.enumerateDevices?.();
    return devices ? devices.filter((d) => d.kind === "videoinput").length : null;
  });
  const connection = nav.connection
    ? {
        type: nav.connection.type || null,
        effectiveType: nav.connection.effectiveType || null,
        downlinkMbps: nav.connection.downlink ?? null,
        rttMs: nav.connection.rtt ?? null,
      }
    : null;

  const fullVersion = hints?.fullVersionList?.find((b) => !/Not.?A.?Brand|Chromium/i.test(b.brand));

  return {
    make: guessMake(model, ua),
    model,
    os: hints?.platform
      ? `${hints.platform}${hints.platformVersion ? ` ${hints.platformVersion}` : ""}`
      : null,
    architecture: hints?.architecture
      ? `${hints.architecture}${hints.bitness ? ` ${hints.bitness}-bit` : ""}`
      : null,
    browser: fullVersion ? `${fullVersion.brand} ${fullVersion.version}` : browserFromUa(ua),
    userAgent: ua,
    screen: window.screen ? `${window.screen.width} x ${window.screen.height}` : null,
    viewport: `${window.innerWidth} x ${window.innerHeight}`,
    pixelRatio: window.devicePixelRatio || null,
    orientation: window.screen?.orientation?.type || null,
    touchPoints: nav.maxTouchPoints ?? null,
    cpuCores: nav.hardwareConcurrency ?? null,
    memoryGb: nav.deviceMemory ?? null,
    cameras,
    battery,
    connection,
    online: nav.onLine,
    storage,
    language: nav.language || null,
    timeZone: (() => {
      try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
      } catch {
        return null;
      }
    })(),
    localTime: new Date().toString(),
    address: window.location.origin,
    appVersion: version?.commit || null,
    deviceCode: deviceIdentifier || null,
    standalone: window.matchMedia?.("(display-mode: standalone)")?.matches || false,
  };
};

// Labels for the settings Debug tab and the tablet's (i) panel, in display order.
export const DEVICE_INFO_FIELDS = [
  ["make", "Make"],
  ["model", "Model"],
  ["os", "Operating system"],
  ["browser", "Browser"],
  ["architecture", "Architecture"],
  ["screen", "Screen"],
  ["viewport", "Browser window"],
  ["pixelRatio", "Pixel ratio"],
  ["orientation", "Orientation"],
  ["touchPoints", "Touch points"],
  ["cpuCores", "CPU cores"],
  ["memoryGb", "Memory (GB, approx.)"],
  ["cameras", "Cameras"],
  ["battery", "Battery"],
  ["connection", "Network"],
  ["online", "Online"],
  ["storage", "Browser storage"],
  ["language", "Language"],
  ["timeZone", "Time zone"],
  ["localTime", "Device clock"],
  ["address", "Kiosk address"],
  ["appVersion", "App version"],
  ["deviceCode", "Device code"],
  ["standalone", "Installed as app"],
  ["userAgent", "User agent"],
];

export const formatDeviceValue = (key, value) => {
  if (value === null || value === undefined || value === "") return "-";
  if (key === "battery") return `${value.levelPercent}%${value.charging ? ", charging" : ""}`;
  if (key === "connection")
    return [
      value.type,
      value.effectiveType,
      value.downlinkMbps != null ? `${value.downlinkMbps} Mbps` : null,
      value.rttMs != null ? `${value.rttMs} ms` : null,
    ]
      .filter(Boolean)
      .join(", ") || "-";
  if (key === "storage") return `${value.usedMb} MB of ${value.quotaMb} MB`;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};
